import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Github, Mail, Rss, ChevronDown } from "lucide-react";
import {
  fetchPublicSiteConfig,
  type SiteConfig,
  type SiteContactItem,
} from "@/api/site-config.api";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [contacts, setContacts] = useState<SiteContactItem[]>([]);
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
  const [mobileOpenSection, setMobileOpenSection] = useState<string | null>(
    "博客",
  );

  useEffect(() => {
    let cancelled = false;
    fetchPublicSiteConfig().then((config) => {
      if (!cancelled) {
        setContacts(Array.isArray(config.contacts) ? config.contacts : []);
        setSiteConfig(config);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const mail = useMemo(
    () => contacts.find((item) => (item.href || "").startsWith("mailto:")),
    [contacts],
  );
  const github = useMemo(
    () => contacts.find((item) => /github/i.test(item.label)),
    [contacts],
  );

  const socialLinks = [
    {
      name: "GitHub",
      icon: Github,
      url: github?.href || "https://github.com",
      color: "hover:text-gray-900 dark:hover:text-white",
    },
    {
      name: "邮箱",
      icon: Mail,
      url: mail?.href || "mailto:contact@yuguige.com",
      color: "hover:text-blue-500",
    },
    {
      name: "RSS",
      icon: Rss,
      url: "/rss.xml",
      color: "hover:text-orange-500",
    },
  ];

  const featureLinks = [
    { name: "时间轴", url: "/timeline" },
    ...(siteConfig?.music?.enabled ? [{ name: "音乐播放器", url: "__music_open__" }] : []),
    ...(Array.isArray(siteConfig?.labModules) && siteConfig.labModules.length > 0
      ? [{ name: "实验室", url: "/lab" }]
      : []),
    ...(siteConfig?.effects?.enabled ? [{ name: "界面设置", url: "__floating_menu__" }] : []),
  ];

  const footerLinks = [
    {
      title: "博客",
      links: [
        { name: "最新文章", url: "/" },
        { name: "文章归档", url: "/archive" },
        { name: "分类目录", url: "/archive" },
        { name: "标签云", url: "/tags" },
      ],
    },
    {
      title: "功能",
      links: featureLinks,
    },
    {
      title: "关于",
      links: [
        { name: "关于我", url: "/about" },
        { name: "联系方式", url: "/contact" },
        { name: "友情链接", url: "/links" },
        { name: "网站统计", url: "/stats" },
      ],
    },
  ].filter((group) => group.links.length > 0);

  const handleFooterAction = (url: string) => {
    if (url === "__theme__" || url === "__floating_menu__") {
      window.dispatchEvent(new Event("floating-settings:open"));
      return;
    }
    if (url === "__music_open__") {
      window.dispatchEvent(new Event("footer:open-music-player"));
      return;
    }
  };

  const renderFooterLink = (name: string, url: string, isMobile = false) => {
    if (url === "__theme__" || url === "__floating_menu__" || url === "__music_open__") {
      return (
        <button
          type="button"
          onClick={() => {
            handleFooterAction(url);
            if (isMobile) setMobileOpenSection(null);
          }}
          className="text-[var(--text-secondary)] text-sm hover:text-[var(--text-primary)] transition-colors"
        >
          {name}
        </button>
      );
    }

    return (
      <Link
        to={url}
        onClick={() => {
          if (isMobile) setMobileOpenSection(null);
        }}
        className="text-[var(--text-secondary)] text-sm hover:text-[var(--text-primary)] transition-colors"
      >
        {name}
      </Link>
    );
  };

  return (
    <footer className="bg-[var(--bg-card)] border-t border-[var(--border-light)] mt-auto">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div className="lg:col-span-1">
            <div className="flex flex-col items-start space-x-2 mb-4">
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
                  羽归阁
                </span>
                <span className="hidden sm:inline text-[11px] uppercase tracking-[0.22em] text-blue-500/80">
                  YUGUIGE
                </span>
              </div>
              <p className="hidden sm:block text-[11px] text-[var(--text-secondary)]">
                写字 · 记事 · 拾光
              </p>
            </div>
            <p className="text-[var(--text-secondary)] text-sm mb-4 leading-relaxed">
              一个持续更新的个人博客，用来记录技术、生活与阶段性的思考。
              这里不追求复杂平台能力，只保留真正常用、顺手的表达工具。
            </p>
            <div className="flex space-x-3">
              {socialLinks.map(({ name, icon: Icon, url, color }) => (
                <a
                  key={name}
                  href={url}
                  target={
                    url.startsWith("/") || url.startsWith("mailto:")
                      ? undefined
                      : "_blank"
                  }
                  rel={
                    url.startsWith("/") || url.startsWith("mailto:")
                      ? undefined
                      : "noopener noreferrer"
                  }
                  title={name}
                  className={`
                    p-2 rounded-lg text-[var(--text-secondary)] transition-all duration-200
                    hover:bg-gray-100 dark:hover:bg-gray-800 ${color}
                  `}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div className="hidden md:contents">
            {footerLinks.map(({ title, links }) => (
              <div key={title}>
                <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                  {title}
                </h4>
                <ul className="space-y-2">
                  {links.map(({ name, url }) => (
                    <li key={name}>{renderFooterLink(name, url, false)}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="md:hidden mb-8">
          {footerLinks.map(({ title, links }) => {
            const opened = mobileOpenSection === title;
            return (
              <div
                key={title}
                className="border-b border-[var(--border-light)] bg-transparent"
              >
                <button
                  type="button"
                  className="w-full px-4 py-3 flex items-center justify-between text-left"
                  onClick={() =>
                    setMobileOpenSection((prev) => (prev === title ? null : title))
                  }
                >
                  <span className="font-semibold text-[var(--text-primary)]">
                    {title}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-[var(--text-secondary)] transition-transform duration-200 ${
                      opened ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-out ${
                    opened ? "max-h-64 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
                  }`}
                >
                  <ul className="px-4 pb-3 space-y-2">
                    {links.map(({ name, url }) => (
                      <li key={name}>{renderFooterLink(name, url, true)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-[var(--border-light)] pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-1 text-sm text-[var(--text-secondary)]">
              <span>© {currentYear} 羽归阁. Made with</span>
              <Heart size={14} className="text-red-500 mx-1" />
              <span>by</span>
              <a
                href="/about"
                className="text-[var(--text-primary)] hover:text-blue-500 transition-colors font-medium"
              >
                羽归阁
              </a>
            </div>

            <div className="flex items-center space-x-4 text-xs text-[var(--text-muted)]">
              <span>Powered by</span>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                  React
                </span>
                <span className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded">
                  TypeScript
                </span>
                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                  Parse Server
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
